import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, setToken } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Radar from "@/pages/Radar";
import Pipeline from "@/pages/Pipeline";
import Benchmark from "@/pages/Benchmark";
import Produtos from "@/pages/Produtos";
import Fornecedores from "@/pages/Fornecedores";
import Financeiro from "@/pages/Financeiro";
import Calculadora from "@/pages/Calculadora";
import Configuracoes from "@/pages/Configuracoes";
import Participar from "@/pages/Participar";
import NotaFiscal from "@/pages/NotaFiscal";
import Placeholder from "@/pages/Placeholder";

function FullLoader() {
  return (
    <div className="grid min-h-screen place-items-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        if (sid) {
          const { data } = await api.post("/auth/google/session", { session_id: sid });
          setToken(data.token);
          setUser(data);
        }
      } catch {}
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/dashboard", { replace: true });
    })();
  }, [navigate, setUser]);
  return <FullLoader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/radar" element={<Radar />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/benchmark" element={<Benchmark />} />
        <Route path="/produtos" element={<Produtos />} />
        <Route path="/fornecedores" element={<Fornecedores />} />
        <Route path="/financeiro" element={<Financeiro />} />
        <Route path="/calculadora" element={<Calculadora />} />
        <Route path="/configuracoes" element={<Configuracoes />} />
        <Route path="/participar/:id" element={<Participar />} />
        <Route path="/nfe" element={<NotaFiscal />} />
        <Route path="/crm" element={<Placeholder title="CRM" desc="Gestão de contatos com órgãos públicos, fornecedores e parceiros." />} />
        <Route path="/marketplaces" element={<Placeholder title="Central de Marketplaces" desc="Mercado Livre, Amazon, Shopee — publicação, estoque e pedidos unificados." />} />
        <Route path="/relatorios" element={<Placeholder title="Relatórios & PRO INVICTA Intelligence" desc="Inteligência do negócio com respostas sobre órgãos, produtos, ticket médio e margens." />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
